import * as React from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { useOfflineFilesStore } from "~/config/offline-storage/offlineFiles.store";
import { useIsOffline } from "~/config/offline-storage/useIsOffline.hook";
import { Box } from "~/design-system/Box";
import { useBreakpoint } from "~/design-system/hooks";
import { createComponentHook } from "~/types";
import { useFolderNamesStore } from "../../screens/ProjectDocumentsScreen/components/FilesFolders/FilesFolders.store";
import { Breadcrumb } from "./components/Breadcrumb/Breadcrumb";
import { FilesFoldersNavigatorList } from "./components/FilesFoldersNavigatorList/FilesFoldersNavigatorList";
import type { GetFilesFoldersNavigatorQuery } from "./FilesFoldersNavigator.graphql";
import {
  useGetFilesFoldersNavigatorQuery,
  useGetProjectFileRootQuery,
} from "./FilesFoldersNavigator.graphql";
import { useFileFoldersNavigatorContext } from "./FilesFoldersNavigatorContext";

type File = {
  file: NonNullable<
    GetFilesFoldersNavigatorQuery["folders_by_pk"]
  >["files"][number];
  type: "file";
  disabled?: boolean;
};

type Folder = {
  folder: NonNullable<
    GetFilesFoldersNavigatorQuery["folders_by_pk"]
  >["folders"][number];
  type: "folder";
};

type FileFolder = File | Folder;
type NavigatorFile = NonNullable<
  GetFilesFoldersNavigatorQuery["folders_by_pk"]
>["files"][number];
type NavigatorFolder = NonNullable<
  GetFilesFoldersNavigatorQuery["folders_by_pk"]
>["folders"][number];

const useFilesFoldersNavigator = createComponentHook(
  (props: FilesFoldersNavigatorProps) => {
    const { t } = useTranslation();
    const { breakpoint } = useBreakpoint();
    const { projectId } = useParams();

    const isOffline = useIsOffline();
    const { getFilesOfProject } = useOfflineFilesStore();

    const excludeDriveItems = props.excludeDriveItems ?? false;
    const driveFoldersOnly = props.driveFoldersOnly ?? false;
    const foldersFilter = props.foldersFilter;
    const { folderId, folderName, setFolderName } =
      useFileFoldersNavigatorContext((state) => ({
        folderId: state.folderId,
        folderName: state.folderName,
        setFolderName: state.setFolderName,
      }));

    const [{ data: projectRootData }] = projectId
      ? useGetProjectFileRootQuery({
          variables: {
            project_id: projectId,
          },
        })
      : [{ data: null }];

    const [{ data }] = useGetFilesFoldersNavigatorQuery({
      pause: isOffline || !folderId,
      requestPolicy: "cache-first",
      variables: { folder_id: folderId },
    });

    const { folderNames } = useFolderNamesStore();
    const parentIds = [
      ...((data?.folders_by_pk?.parents_ids as string[]) ?? ([] as string[])),
    ];
    parentIds.push(folderId);

    const breadCrumbData =
      parentIds?.map((id) => ({
        id,
        name: folderNames.get(id) ?? "root",
        // TODO: retrieve those value from api instead of this placeholder
        strongNotification: 0,
        weakNotif: false,
      })) ?? [];

    const rawFiles = React.useMemo<NavigatorFile[]>(() => {
      if (isOffline) {
        return getFilesOfProject(projectId ?? "").map((file) => {
          const {
            file: { files_by_pk },
          } = file;
          const fileExtension = files_by_pk?.name?.split(".").pop() ?? "";
          return {
            due_date: files_by_pk?.due_date ?? "",
            extension: fileExtension,
            id: files_by_pk?.id ?? "",
            last_version: [
              {
                created_at: files_by_pk?.last_version?.[0]?.created_at ?? "",
                creator: files_by_pk?.last_version?.[0]?.creator ?? {
                  full_name: "",
                  user_avatar: {
                    public_url: "",
                  },
                },
                id: files_by_pk?.last_version?.[0]?.id ?? "",
                size: file.size,
                updated_at: files_by_pk?.last_version?.[0]?.updated_at ?? "",
                url: files_by_pk?.last_version?.[0]?.url ?? "",
              },
            ],

            name: files_by_pk?.name ?? "",
          } as NavigatorFile;
        });
      }
      if (!data?.folders_by_pk) return [];
      return data.folders_by_pk.files ?? [];
    }, [data?.folders_by_pk, isOffline, getFilesOfProject, projectId]);

    const rawFolders = React.useMemo<NavigatorFolder[]>(
      () => data?.folders_by_pk?.folders ?? [],
      [data?.folders_by_pk?.folders]
    );
    const driveFileIds = React.useMemo<Set<string>>(
      () =>
        new Set(
          (rawFiles ?? [])
            .filter((file) => Boolean(file.drive_file_mapping?.id))
            .map((file) => file.id)
        ),
      [rawFiles]
    );
    const driveFolderIds = React.useMemo<Set<string>>(
      () =>
        new Set(
          (rawFolders ?? [])
            .filter((folder) => Boolean(folder.drive_folder_mapping?.id))
            .map((folder) => folder.id)
        ),
      [rawFolders]
    );
    const files = React.useMemo<NavigatorFile[]>(() => {
      if (isOffline || !excludeDriveItems) {
        return rawFiles;
      }
      return rawFiles.filter((file) => !driveFileIds.has(file.id));
    }, [driveFileIds, excludeDriveItems, isOffline, rawFiles]);
    const folders = React.useMemo<NavigatorFolder[]>(() => {
      if (isOffline || (!excludeDriveItems && !driveFoldersOnly)) {
        return rawFolders;
      }
      if (driveFoldersOnly) {
        return rawFolders.filter((folder) => driveFolderIds.has(folder.id));
      }
      return rawFolders.filter((folder) => !driveFolderIds.has(folder.id));
    }, [
      driveFoldersOnly,
      driveFolderIds,
      excludeDriveItems,
      isOffline,
      rawFolders,
    ]);
    const filteredFolders = React.useMemo(
      () => foldersFilter?.(folders) ?? folders,
      [folders, foldersFilter]
    );
    const currentFolderName = React.useMemo(
      () => (folderId ? folderNames.get(folderId) : undefined),
      [folderId, folderNames]
    );

    React.useEffect(() => {
      if (!folderId) return;

      const newName =
        projectRootData?.projects_by_pk?.root_folder_id === folderId
          ? "root"
          : currentFolderName ?? "root";

      if (newName !== folderName) {
        setFolderName(newName);
      }
    }, [
      folderId,
      folderName,
      currentFolderName,
      folderNames,
      projectRootData?.projects_by_pk?.root_folder_id,
      setFolderName,
    ]);

    return {
      state: {
        breadCrumbData,
        breakpoint,
        files,
        folders: filteredFolders,
      },
      t,
    };
  }
);

function FilesFoldersNavigatorContent(props: FilesFoldersNavigatorProps) {
  const { state } = useFilesFoldersNavigator(props);

  const isOffline = useIsOffline();
  return (
    <>
      {!isOffline && <Breadcrumb folders={state.breadCrumbData} />}
      <Box css={{ height: "100%", overflow: "auto" }}>
        <FilesFoldersNavigatorList
          filesLocalizable={props.filesLocalizable}
          isFileSelectable={props.isFileSelectable}
          filesDisabled={props.filesDisabled}
          files={state.files}
          folders={state.folders}
          hiddenFiles={props.hiddenFiles}
        />
      </Box>
    </>
  );
}

interface FilesFoldersNavigatorProps {
  height?: string;
  foldersFilter?: (folders: Array<Folder["folder"]>) => Array<Folder["folder"]>;
  filesDisabled?: boolean;
  filesLocalizable?: boolean;
  isFileSelectable?: (fileName: string) => boolean;
  hiddenFiles?: boolean;
  isOffline?: boolean;
  excludeDriveItems?: boolean;
  driveFoldersOnly?: boolean;
}

function FilesFoldersNavigatorComponent(props: FilesFoldersNavigatorProps) {
  const isOffline = useIsOffline();

  return (
    // Replace a Stack but with a defined height for virtualized list in pop-up
    <Box
      css={{
        display: "grid",
        gridRowGap: "$gutter",
        gridTemplateRows: isOffline ? "auto" : "$buttonHeight auto",
        height: props.height,
      }}
    >
      <FilesFoldersNavigatorContent {...props} />
    </Box>
  );
}

function FilesFoldersNavigator(props: FilesFoldersNavigatorProps) {
  const { foldersFilter, height } = props;
  return (
    <FilesFoldersNavigatorComponent
      height={height ?? "100%"}
      foldersFilter={foldersFilter}
      filesDisabled={props.filesDisabled}
      hiddenFiles={props.hiddenFiles}
      filesLocalizable={props.filesLocalizable}
      isFileSelectable={props.isFileSelectable}
      excludeDriveItems={props.excludeDriveItems}
      driveFoldersOnly={props.driveFoldersOnly}
    />
  );
}

export type { File, FileFolder, FilesFoldersNavigatorProps, Folder };
export { FilesFoldersNavigator, useFilesFoldersNavigator };
